import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import FrontechLogo from './FrontechLogo';
import {
  ArrowLeft, Edit, Copy, Check, Mic, Send, X,
  MessageSquare, Square, Globe, Upload, Loader,
  BarChart3, Zap, Code2, Layout, ShoppingBag, Smartphone, Repeat2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = 'https://frontech-consulting.com';

/* ──────────────────────────────────────────────────────────────
   Inline voice widget (for the Test tab)
────────────────────────────────────────────────────────────── */
function VoiceWidget({ agentId, agentConfig, onClose }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [status, setStatus] = useState('');
  const [connected, setConnected] = useState(false);
  const [convoMode, setConvoMode] = useState(false);
  const wsRef = useRef(null);
  const recognitionRef = useRef(null);
  const msgsContainerRef = useRef(null);
  const convoModeRef = useRef(false);

  const scrollToBottom = () => {
    if (msgsContainerRef.current) msgsContainerRef.current.scrollTop = msgsContainerRef.current.scrollHeight;
  };
  useEffect(scrollToBottom, [messages]);
  useEffect(() => { convoModeRef.current = convoMode; }, [convoMode]);

  const startRecording = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setStatus('Voice requires Chrome or Edge'); return; }
    const rec = new SR();
    rec.lang = 'en-US';
    rec.interimResults = false;
    rec.onresult = (e) => {
      const text = e.results[0][0].transcript;
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'text', text }));
        setStatus('Processing…');
      }
    };
    rec.onerror = () => setIsRecording(false);
    rec.onend = () => setIsRecording(false);
    rec.start();
    recognitionRef.current = rec;
    setIsRecording(true);
  }, []);

  const stopRecording = useCallback(() => {
    recognitionRef.current?.stop();
    setIsRecording(false);
  }, []);

  const connect = useCallback(() => {
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/conversation/${agentId}`);
    ws.onopen = () => setConnected(true);
    ws.onclose = () => setConnected(false);
    ws.onerror = () => setConnected(false);
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'welcome') { setMessages([{ role: 'assistant', content: data.message }]); setStatus(''); }
      else if (data.type === 'transcription') { setMessages(p => [...p, { role: 'user', content: data.text }]); setStatus(''); }
      else if (data.type === 'status') { setStatus(data.message); }
      else if (data.type === 'ai_response') { setMessages(p => [...p, { role: 'assistant', content: data.text }]); setStatus(''); }
      else if (data.type === 'audio') {
        const audio = new Audio(`data:audio/mp3;base64,${data.data}`);
        audio.play().catch(() => {});
      } else if (data.type === 'audio_end') {
        if (convoModeRef.current) setTimeout(() => startRecording(), 300);
      }
    };
    wsRef.current = ws;
  }, [agentId, startRecording]);

  useEffect(() => { connect(); return () => wsRef.current?.close(); }, [connect]);

  const sendText = () => {
    if (!inputText.trim() || !wsRef.current) return;
    wsRef.current.send(JSON.stringify({ type: 'text', text: inputText.trim() }));
    setInputText('');
    setStatus('Processing…');
  };

  const color = agentConfig?.widget_color || '#00D67E';

  return (
    <div data-testid="voice-widget-panel"
      className="fixed bottom-6 right-6 w-96 max-w-[calc(100vw-3rem)] flex flex-col z-[9999] rounded-2xl overflow-hidden shadow-2xl"
      style={{ height: 520, background: '#0C1117', border: '1px solid rgba(255,255,255,0.08)' }}>

      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)', background: `${color}10` }}>
        <div className="w-2.5 h-2.5 rounded-full transition-colors" style={{ background: connected ? color : '#444' }} />
        <span className="text-sm font-semibold text-white flex-1">{agentConfig?.widget_title || 'Voice Assistant'}</span>
        <button
          onClick={() => setConvoMode(m => !m)}
          title={convoMode ? 'Conversation mode on' : 'Enable conversation mode'}
          className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-full transition-all mr-1"
          style={{
            background: convoMode ? `${color}20` : 'rgba(255,255,255,0.06)',
            border: convoMode ? `1px solid ${color}40` : '1px solid rgba(255,255,255,0.12)',
            color: convoMode ? color : '#666',
          }}>
          <Repeat2 size={11} />
          Convo
        </button>
        <button onClick={onClose} data-testid="widget-close-btn" className="text-zinc-500 hover:text-white transition-colors">
          <X size={17} />
        </button>
      </div>

      {/* Messages */}
      <div ref={msgsContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className="max-w-[80%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed"
              style={{
                background: msg.role === 'user' ? `${color}25` : 'rgba(255,255,255,0.06)',
                color: '#c9d6e3',
                border: msg.role === 'user' ? `1px solid ${color}40` : '1px solid rgba(255,255,255,0.07)',
              }}>
              {msg.content}
            </div>
          </div>
        ))}
        {status && <div className="text-center text-xs py-1 animate-pulse" style={{ color: '#3d5066' }}>{status}</div>}
      </div>

      {/* Input */}
      <div className="p-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
        <div className="flex items-center gap-2">
          <button data-testid="voice-mic-btn"
            onClick={() => isRecording ? stopRecording() : startRecording()}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all flex-shrink-0"
            style={{ background: isRecording ? '#ef4444' : `${color}20`, color: isRecording ? '#fff' : color }}>
            {isRecording ? <Square size={16} /> : <Mic size={16} />}
          </button>
          <input data-testid="voice-text-input" value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendText()}
            className="flex-1 text-sm rounded-xl px-3 py-2.5 outline-none"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#e2eaf4' }}
            placeholder="Type a message…" />
          <button data-testid="voice-send-btn" onClick={sendText}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all flex-shrink-0"
            style={{ background: color, color: '#000' }}>
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Stat card
────────────────────────────────────────────────────────────── */
function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl p-5" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold tracking-wider uppercase" style={{ color: 'var(--color-text-muted)' }}>{label}</span>
        <Icon size={15} style={{ color: 'var(--color-text-muted)' }} />
      </div>
      <p className="font-heading text-3xl font-bold tracking-tight stat-number">{value}</p>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
   Main component
────────────────────────────────────────────────────────────── */
export default function AgentDetail() {
  const { id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showWelcome, setShowWelcome] = useState(!!location.state?.justCreated);
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWidget, setShowWidget] = useState(false);
  const [tab, setTab] = useState('deploy');

  const [copied, setCopied] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [crawling, setCrawling] = useState(false);
  const [crawlResult, setCrawlResult] = useState(null);
  const [crawlError, setCrawlError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api().get(`/api/agents/${id}`);
        setAgent(data);
      } catch { navigate('/dashboard'); }
      setLoading(false);
    })();
  }, [id, api, navigate]);

  const embedCode = agent
    ? `<script src="${FRONTEND_URL}/widget.js" data-agent-id="${agent.id}" data-backend-url="${BACKEND_URL}" data-color="${agent.widget_color || '#00D67E'}" data-position="${agent.widget_position || 'bottom-right'}" data-title="${agent.widget_title || 'Voice Assistant'}" async></script>`
    : '';

  const copyEmbed = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const crawlWebsite = async () => {
    if (!websiteUrl.trim()) return;
    setCrawling(true); setCrawlError(''); setCrawlResult(null);
    try {
      const { data } = await api().post(`/api/agents/${id}/crawl`, { url: websiteUrl.trim(), max_pages: 15 });
      setCrawlResult(data);
    } catch (err) { setCrawlError(err.response?.data?.detail || 'Failed to crawl website'); }
    setCrawling(false);
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadResult('');
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await api().post(`/api/agents/${id}/knowledge`, fd);
      setUploadResult(`Uploaded ${data.chunks} chunks from "${file.name}"`);
    } catch { setUploadResult('Upload failed. Try a plain .txt or .md file.'); }
    setUploading(false);
    e.target.value = '';
  };

  if (loading || !agent) {
    return (
      <div className="flex items-center justify-center py-24">
        <FrontechLogo size={40} variant="loader" color="var(--color-accent)" />
      </div>
    );
  }

  const color = agent.widget_color || '#00D67E';

  const TABS = [
    { id: 'deploy',    label: 'Deploy',    icon: Code2 },
    { id: 'knowledge', label: 'Knowledge', icon: Globe },
    { id: 'test',      label: 'Test',      icon: Mic },
    { id: 'stats',     label: 'Stats',     icon: BarChart3 },
  ];

  const PLATFORMS = [
    { icon: Layout,      name: 'Any HTML page',   desc: 'Paste before </body> — works everywhere' },
    { icon: ShoppingBag, name: 'Shopify',          desc: 'Themes → Edit code → theme.liquid before </body>' },
    { icon: Smartphone,  name: 'WordPress',        desc: 'Appearance → Theme Editor → footer.php' },
    { icon: Globe,       name: 'Java / Spring',    desc: 'Paste in your layout Thymeleaf/JSP template' },
  ];

  return (
    <div>
      {/* Back */}
      <button onClick={() => navigate('/dashboard')} data-testid="back-from-detail"
        className="flex items-center gap-1.5 text-sm mb-6 transition-colors"
        style={{ color: 'var(--color-text-muted)' }}>
        <ArrowLeft size={15} /> Back to Agents
      </button>

      {/* Header card */}
      <div className="rounded-2xl p-6 mb-6 flex items-center justify-between gap-4"
        style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${color}18`, border: `1.5px solid ${color}30` }}>
            <MessageSquare size={26} style={{ color }} />
          </div>
          <div>
            <h1 data-testid="agent-detail-name"
              className="font-heading text-xl font-bold tracking-tight"
              style={{ color: 'var(--color-text-primary)' }}>
              {agent.name}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ background: `${color}15`, color, border: `1px solid ${color}30` }}>
                {agent.voice_name || 'Rachel'}
              </span>
              <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {agent.total_conversations || 0} convos · {(agent.total_minutes || 0).toFixed(1)} min
              </span>
            </div>
          </div>
        </div>
        <Link to={`/dashboard/agents/${id}/edit`} data-testid="edit-agent-btn"
          className="btn-ghost flex items-center gap-2 text-sm flex-shrink-0">
          <Edit size={15} /> Edit
        </Link>
      </div>

      {/* Post-creation onboarding banner */}
      {showWelcome && (
        <div className="rounded-2xl p-5 mb-6 flex items-start gap-4"
          style={{ background: 'rgba(0,214,126,0.07)', border: '1px solid rgba(0,214,126,0.25)' }}>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
            style={{ background: 'rgba(0,214,126,0.15)' }}>
            <Zap size={18} style={{ color: 'var(--color-accent)' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm mb-1" style={{ color: 'var(--color-accent)' }}>
              Your agent is live!
            </p>
            <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>
              Here's how to get the most out of it:
            </p>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => { setTab('test'); setShowWelcome(false); }}
                className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all"
                style={{ background: 'var(--color-accent)', color: '#000' }}>
                1. Test your agent
              </button>
              <button onClick={() => { setTab('knowledge'); setShowWelcome(false); }}
                className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all"
                style={{ background: 'var(--color-elevated)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)' }}>
                2. Add knowledge
              </button>
              <button onClick={() => { setTab('deploy'); setShowWelcome(false); }}
                className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all"
                style={{ background: 'var(--color-elevated)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border)' }}>
                3. Deploy to your site
              </button>
            </div>
          </div>
          <button onClick={() => setShowWelcome(false)}
            className="text-zinc-500 hover:text-zinc-300 transition-colors flex-shrink-0">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 p-1 rounded-xl w-fit"
        style={{ background: 'var(--color-elevated)', border: '1px solid var(--color-border)' }}>
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: active ? 'var(--color-surface)' : 'transparent',
                color: active ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                boxShadow: active ? '0 1px 4px rgba(0,0,0,0.2)' : 'none',
              }}>
              <Icon size={14} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ── DEPLOY TAB ── */}
      {tab === 'deploy' && (
        <div className="space-y-5">
          {/* Step 1 — train */}
          <div className="rounded-2xl p-6" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0"
                style={{ background: color, color: '#000' }}>1</div>
              <h3 className="font-heading text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Train on your website
              </h3>
            </div>
            <p className="text-sm mb-4 ml-9" style={{ color: 'var(--color-text-secondary)' }}>
              Enter your URL and we'll read every page so your agent knows your business.
            </p>
            <div className="flex gap-3 ml-9">
              <input value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && crawlWebsite()}
                placeholder="https://yourbusiness.com"
                className="input-field focus-ring-accent flex-1" />
              <button onClick={crawlWebsite} disabled={crawling || !websiteUrl.trim()}
                className="btn-accent flex items-center gap-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0">
                {crawling ? <><Loader size={14} className="animate-spin" />Crawling…</> : <><Globe size={14} />Crawl Site</>}
              </button>
            </div>
            {crawlResult && (
              <div className="mt-3 ml-9 text-sm rounded-xl px-4 py-2.5"
                style={{ background: 'var(--color-accent-dim)', border: '1px solid rgba(0,214,126,0.2)', color: 'var(--color-accent)' }}>
                <Zap size={13} className="inline mr-1.5" />
                Read {crawlResult.pages_crawled} pages · {crawlResult.chunks_stored} knowledge chunks stored
              </div>
            )}
            {crawlError && (
              <div className="mt-3 ml-9 text-sm rounded-xl px-4 py-2.5"
                style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171' }}>
                {crawlError}
              </div>
            )}
          </div>

          {/* Step 2 — embed */}
          <div className="rounded-2xl p-6" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0"
                style={{ background: color, color: '#000' }}>2</div>
              <h3 className="font-heading text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Add to your website
              </h3>
            </div>
            <p className="text-sm mb-4 ml-9" style={{ color: 'var(--color-text-secondary)' }}>
              One line of code. Paste it before <code className="font-mono text-xs px-1.5 py-0.5 rounded" style={{ background: 'var(--color-elevated)' }}>&lt;/body&gt;</code> on any platform.
            </p>

            {/* Code block */}
            <div className="ml-9 relative">
              <pre className="text-xs font-mono rounded-xl p-4 overflow-x-auto leading-relaxed whitespace-pre-wrap break-all"
                style={{ background: 'var(--color-elevated)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)' }}>
                {embedCode}
              </pre>
              <button onClick={copyEmbed} data-testid="copy-embed-btn"
                className="absolute top-3 right-3 flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-all"
                style={{
                  background: copied ? 'var(--color-accent-dim)' : 'var(--color-surface)',
                  color: copied ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                  border: '1px solid var(--color-border)',
                }}>
                {copied ? <><Check size={12} />Copied</> : <><Copy size={12} />Copy</>}
              </button>
            </div>

            {/* Platform grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5 ml-9">
              {PLATFORMS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div key={i} className="flex items-start gap-3 rounded-xl p-3.5 transition-colors"
                    style={{ background: 'var(--color-elevated)', border: '1px solid var(--color-border)' }}>
                    <Icon size={15} style={{ color: 'var(--color-accent)', flexShrink: 0, marginTop: 1 }} />
                    <div>
                      <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{p.name}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{p.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Step 2: Zero-Code via Google Tag Manager */}
          <div className="rounded-2xl p-6" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{ background: 'var(--color-accent-dim)', color: 'var(--color-accent)' }}>2</div>
              <div>
                <h3 className="font-heading text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                  Zero-Code Setup via Google Tag Manager
                </h3>
                <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                  No coding required — works on Shopify, WordPress, Wix, Squarespace, and any GTM-enabled site
                </p>
              </div>
            </div>

            <ol className="space-y-3 ml-10">
              {[
                { step: '1', text: 'Open Google Tag Manager → Tags → New → Custom HTML' },
                { step: '2', text: 'Paste the embed code from Step 1 above into the HTML field' },
                { step: '3', text: 'Set trigger to "All Pages"' },
                { step: '4', text: 'Save → Submit → Publish' },
              ].map(({ step, text }) => (
                <li key={step} className="flex items-start gap-3 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full text-xs font-semibold flex-shrink-0 mt-0.5"
                    style={{ background: 'var(--color-elevated)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)' }}>
                    {step}
                  </span>
                  {text}
                </li>
              ))}
            </ol>

            <div className="mt-4 ml-10 rounded-xl px-4 py-3 text-xs"
              style={{ background: 'var(--color-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-muted)' }}>
              <span style={{ color: 'var(--color-accent)' }}>No GTM yet?</span> Go to{' '}
              <span className="font-mono" style={{ color: 'var(--color-text-secondary)' }}>tagmanager.google.com</span>
              , create a free account, then add the GTM snippet to your site header (one-time only).
              After that, all future widget updates deploy without touching your code.
            </div>
          </div>
        </div>
      )}

      {/* ── KNOWLEDGE TAB ── */}
      {tab === 'knowledge' && (
        <div className="space-y-5">
          {/* Upload */}
          <div className="rounded-2xl p-6" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
            <h3 className="font-heading text-base font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>
              Upload Documents
            </h3>
            <p className="text-sm mb-5" style={{ color: 'var(--color-text-secondary)' }}>
              Upload text files, FAQs, or docs to expand the agent's knowledge beyond your website.
            </p>
            <input ref={fileInputRef} type="file" accept=".txt,.pdf,.md,.csv" onChange={uploadFile} className="hidden" />
            <button onClick={() => fileInputRef.current?.click()} disabled={uploading}
              className="btn-accent flex items-center gap-2 text-sm disabled:opacity-40">
              {uploading ? <><Loader size={14} className="animate-spin" />Uploading…</> : <><Upload size={14} />Upload File</>}
            </button>
            {uploadResult && (
              <p className="mt-3 text-sm" style={{ color: 'var(--color-accent)' }}>{uploadResult}</p>
            )}
          </div>

          {/* Crawl */}
          <div className="rounded-2xl p-6" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
            <h3 className="font-heading text-base font-semibold mb-1" style={{ color: 'var(--color-text-primary)' }}>
              Crawl a Website
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
              Re-crawl your website to refresh or update the agent's knowledge.
            </p>
            <div className="flex gap-3">
              <input value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && crawlWebsite()}
                placeholder="https://yourbusiness.com"
                className="input-field focus-ring-accent flex-1" />
              <button onClick={crawlWebsite} disabled={crawling || !websiteUrl.trim()}
                className="btn-accent flex items-center gap-2 text-sm disabled:opacity-40 flex-shrink-0">
                {crawling ? <><Loader size={14} className="animate-spin" />Crawling…</> : <><Globe size={14} />Crawl</>}
              </button>
            </div>
            {crawlResult && (
              <p className="mt-3 text-sm" style={{ color: 'var(--color-accent)' }}>
                Done — {crawlResult.pages_crawled} pages, {crawlResult.chunks_stored} chunks stored.
              </p>
            )}
            {crawlError && (
              <p className="mt-3 text-sm" style={{ color: '#f87171' }}>{crawlError}</p>
            )}
          </div>
        </div>
      )}

      {/* ── TEST TAB ── */}
      {tab === 'test' && (
        <div className="rounded-2xl p-8 text-center" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
          <div className="flex justify-center mb-5">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
              style={{ background: `${color}15`, border: `1.5px solid ${color}30` }}>
              <Mic size={28} style={{ color }} />
            </div>
          </div>
          <h3 className="font-heading text-lg font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            Test your agent live
          </h3>
          <p className="text-sm mb-6 max-w-xs mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
            Talk to your agent exactly as your customers will — via voice or text.
          </p>
          <button onClick={() => setShowWidget(!showWidget)} data-testid="test-widget-btn"
            className="btn-accent flex items-center gap-2 text-sm mx-auto">
            <Mic size={15} />
            {showWidget ? 'Close Widget' : 'Open Test Widget'}
          </button>
          {showWidget && (
            <p className="mt-4 text-xs" style={{ color: 'var(--color-text-muted)' }}>
              Widget is live in the bottom-right corner of this page.
            </p>
          )}
        </div>
      )}

      {/* ── STATS TAB ── */}
      {tab === 'stats' && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard label="Messages" value={agent.total_messages || 0} icon={MessageSquare} />
            <StatCard label="Conversations" value={agent.total_conversations || 0} icon={BarChart3} />
            <StatCard label="Minutes Used" value={(agent.total_minutes || 0).toFixed(1)} icon={Zap} />
          </div>
          <div className="rounded-2xl p-6" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)' }}>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Voice: <span style={{ color: 'var(--color-text-primary)' }}>{agent.voice_name || 'Rachel'}</span>
              {' · '}
              Widget: <span style={{ color: 'var(--color-text-primary)' }}>{agent.widget_title}</span>
              {' · '}
              Position: <span style={{ color: 'var(--color-text-primary)' }}>{agent.widget_position}</span>
            </p>
          </div>
        </div>
      )}

      {/* Inline test widget */}
      {showWidget && (
        <VoiceWidget agentId={id} agentConfig={agent} onClose={() => setShowWidget(false)} />
      )}
    </div>
  );
}
